/**
 * placementEditStore — tiny shared store for an IN-CONTEXT placement edit
 * session. Both the draggable surface layers (Grid `GridArtworkLayer`, Canvas
 * `CanvasFrozenArtworkLayer`) and the shared `PlacementControls` panel read/
 * write the SAME live edit state, so the artwork drags on the real surface and
 * the panel's sliders (rotation / empty-area opacity) update it live — without
 * prop-drilling through the viewer. Backend remains the sole validity/commit
 * authority; this store only holds the in-flight (unsaved) edit.
 */
import type { PixelContent } from "../../lib/artwork-api";

export interface PlacementEditState {
  artworkId: string;
  ax: number;
  ay: number;
  rotDeg: number;
  bg: string | null;
  opacityPct: number;
  width: number;
  height: number;
  content?: PixelContent; // immutable pixel content (for overview-zoom rendering)
  valid: boolean; // last authoritative preview result (advisory tint only)
}

type Listener = () => void;

let state: PlacementEditState | null = null;
const listeners = new Set<Listener>();
const emit = () => listeners.forEach((l) => l());

export const placementEditStore = {
  get: (): PlacementEditState | null => state,
  subscribe: (l: Listener): (() => void) => {
    listeners.add(l);
    return () => { listeners.delete(l); };
  },
  start: (s: PlacementEditState) => { state = s; emit(); },
  update: (patch: Partial<PlacementEditState>) => {
    if (!state) return;
    state = { ...state, ...patch };
    emit();
  },
  stop: (artworkId?: string) => {
    if (artworkId && state && state.artworkId !== artworkId) return;
    state = null;
    emit();
  },
};
